import { convert } from './engine/convert';
import { CancelledError, loadEngine } from './engine/ffmpeg';
import { inspectFile, type MediaInfo } from './engine/inspect';
import { planConversion, type OutputFormat, type PlanOptions } from './engine/plan';
import { probeWebCodecs } from './engine/webcodecs';
import {
  renderConverting,
  renderDone,
  renderError,
  renderIdle,
  renderInspect,
  renderWarming,
  type ToolPreset,
} from './ui/stages';

// Wave used to cap inputs at 1.5 GB because the whole file had to fit in
// the wasm heap for the ffmpeg-only convert path. With WORKERFS mounts the
// source stays in the browser's Blob store (read on demand, never copied
// wholesale into wasm), Path B's segmented decode keeps peak raw memory in
// the ~128 MB range, and the FSA streaming target lets the output land on
// disk instead of RAM — so the practical ceiling is much higher. 8 GB is a
// deliberately conservative round number: it clears the vast majority of
// real "why isn't my file accepted?" reports without inviting people to
// drop 40 GB Blu-ray rips onto a browser tab.
const HARD_LIMIT_BYTES = 8 * 1024 * 1024 * 1024; // 8 GB
const WARN_LIMIT_BYTES = 2 * 1024 * 1024 * 1024; // 2 GB — warn but don't block
const WARMING_UI_DELAY_MS = 250; // show warming UI only if the wait is real

// Wave converts video → MP4 (primary) or → MP3 (audio extraction bonus).
// Landing pages preselect the intended output via `data-preselect-format`.
const VALID_FORMATS: OutputFormat[] = ['mp4', 'mp3'];
const preselectRaw = (document.body.dataset.preselectFormat ?? '').toLowerCase();
const PRESELECT_FORMAT: OutputFormat =
  (VALID_FORMATS.includes(preselectRaw as OutputFormat) ? preselectRaw : 'mp4') as OutputFormat;
const COMPACT_IDLE = document.body.dataset.compactIdle === 'true';
// Landing pages that promise "video or audio → MP3" set this so the file
// picker advertises audio types and the audio-only rejection is skipped.
// Format-specific pages leave it unset so a .wav dropped on `mp4-to-mp3` is
// still rejected with a helpful hint pointing at this new page.
const ACCEPT_AUDIO = document.body.dataset.acceptAudio === 'true';
// Tool pages (compress, trim, rotate, resize, mute) open the options panel on
// their own section; compress pages for a service also preset its size cap.
const TOOLS: ToolPreset[] = ['compress', 'trim', 'rotate', 'resize', 'mute', 'compat'];
const toolRaw = (document.body.dataset.tool ?? '').toLowerCase();
const TOOL: ToolPreset | null = TOOLS.includes(toolRaw as ToolPreset) ? (toolRaw as ToolPreset) : null;
const TARGET_MB = Number(document.body.dataset.targetMb) || null;

function presetOptions(): PlanOptions {
  if (TOOL === 'compress' && TARGET_MB) return { targetBytes: TARGET_MB * 1_000_000 };
  if (TOOL === 'mute') return { removeAudio: true };
  if (TOOL === 'compat') return { compatibility: true };
  return {};
}

const extensionOf = (name: string): string => {
  const idx = name.lastIndexOf('.');
  return idx >= 0 ? name.slice(idx + 1).toLowerCase() : 'bin';
};

// Video containers Wave accepts. Anything on this list is definitely a video.
// Unknown extensions fall through to a content-based check — ffmpeg auto-detects
// the container from the file bytes, so an odd or missing extension can still
// convert. Pure-audio formats aren't in scope; they're rejected up front.
const KNOWN_VIDEO_EXT = /\.(mp4|m4v|mov|qt|mkv|avi|webm|flv|f4v|ts|m2ts|mts|3gp|3g2|wmv|asf|ogv|ogm|mpg|mpeg|m2v|m1v|vob|mxf|dv|dif|y4m|ivf|rm|rmvb|dav|ps|mp2t|m2t|tp|trp|lrv|360|divx|mk3d|nut|gxf|lxf|nuv|nsv|bik|bk2|smk|roq|mpv|264|h264|265|h265|hevc|vc1|gif)$/i;

// Anything we can identify as clearly-not-a-video is stopped early — images,
// docs, code, archives, executables, pure-audio formats, and playlists /
// manifests. The last group (`m3u8|m3u|pls|xspf|asx|wpl|cue`) is not audio or
// video itself — it's a text file that points at streams — but users drop
// them expecting HLS-style download, which is a wholly different feature.
// Rejecting up front lets us name the mistake instead of returning
// ffmpeg's generic "no streams" error.
const NON_VIDEO_EXT = /\.(mp3|m4a|aac|wav|flac|ogg|opus|wma|aiff|aif|amr|ac3|eac3|dts|ra|caf|au|snd|voc|mka|tta|wv|ape|mp2|mp1|png|jpe?g|bmp|tiff?|webp|heic|heif|svg|ico|avif|pdf|docx?|xlsx?|pptx?|txt|md|rtf|odt|epub|html?|xml|json|yaml|yml|csv|tsv|zip|rar|7z|tar|gz|bz2|xz|dmg|iso|exe|msi|apk|app|dll|so|dylib|jar|py|js|ts|tsx|jsx|cpp|c|h|cs|java|rs|go|rb|php|swift|kt|sh|bat|ps1|m3u8|m3u|pls|xspf|asx|wpl|cue)$/i;

/** Extensions that name a playlist / stream manifest rather than the media
 *  itself. Wave doesn't fetch or stitch HLS segments — the user needs the
 *  actual video file — so we surface a specific message when one of these
 *  is dropped, rather than the catch-all "doesn't look like a video" line. */
const PLAYLIST_EXT = /\.(m3u8|m3u|pls|xspf|asx|wpl|cue)$/i;

let currentFile: File | null = null;
let currentInfo: MediaInfo | null = null;
// Options from the last convert attempt, so "Try again" on the same file
// comes back with the trim / size / rotation the user had set.
let lastOptions: { file: File; options: PlanOptions } | null = null;
let currentAbort: AbortController | null = null;
let engineReady = false;
let enginePrefetch: Promise<unknown> | null = null;

function bootstrap(): void {
  const root = document.getElementById('stage-root');
  if (!root) {
    console.error('[wave] #stage-root missing');
    return;
  }
  // Warm up the WebCodecs probe in the background so future encode paths can
  // check it synchronously. Won't affect first-render latency.
  probeWebCodecs()
    .then((s) => console.info('[wave] webcodecs support', s))
    .catch(() => { /* probe failed silently — fallback path handles it */ });
  goIdle(root);
  // Interactive figures (size calculator, keyframe timeline) live in their
  // own chunk, fetched only by pages that have one.
  if (document.querySelector('[data-explainer]')) void import('./explainers');
}

function setFocusMode(active: boolean): void {
  document.body.classList.toggle('focus-mode', active);
}

function goIdle(root: HTMLElement): void {
  currentFile = null;
  currentInfo = null;
  setFocusMode(false);
  renderIdle(root, {
    compact: COMPACT_IDLE,
    onFile: (file) => { void acceptFile(root, file); },
    onWarmupHint: () => {
      // Kick off engine load in the background as soon as the user shows intent.
      if (engineReady || enginePrefetch) return;
      enginePrefetch = loadEngine()
        .then(() => { engineReady = true; })
        .catch(() => { enginePrefetch = null; });
    },
  });
}

async function acceptFile(root: HTMLElement, file: File): Promise<void> {
  // Second file dropped while another is mid-convert. Silently cancelling
  // the in-flight job would be surprising ("wait, my movie was 40% done"),
  // and queueing is out of scope for Wave's one-file UX. Ask instead.
  if (currentAbort) {
    const proceed = confirm(
      `Another conversion is in progress. Dropping "${file.name}" will cancel it. Continue?`
    );
    if (!proceed) return;
    currentAbort.abort();
    currentAbort = null;
  }
  // On the audio-video-to-MP3 hub the picker also accepts audio sources, so
  // an incoming audio file that isLikelyAudio() flags is welcome. Everywhere
  // else Wave stays video-only and audio files hit the helpful reject below
  // with a nudge toward the wider page.
  const isAudio = isLikelyAudio(file);
  const acceptsThisFile = ACCEPT_AUDIO ? (looksLikeVideo(file) || isAudio) : looksLikeVideo(file);
  if (!acceptsThisFile) {
    setFocusMode(true);
    showError(
      root,
      isLikelyPlaylist(file)
        ? `"${file.name}" is a playlist / manifest, not a video file. Wave doesn't fetch or stitch HLS segments — drop the actual video file the playlist points at.`
        : isAudio
        ? `"${file.name}" looks like an audio file. This page converts video sources — try the /audio-video-to-mp3/ page, which accepts audio too.`
        : ACCEPT_AUDIO
        ? `"${file.name}" doesn't look like a video or audio file. Try an .mp4, .mov, .mkv, .mp3, .wav, .m4a, .flac — or similar.`
        : `Wave converts video into MP4, and "${file.name}" doesn't look like a video. Try an .mp4, .mov, .mkv, .webm, .avi, .wmv — or similar.`
    );
    return;
  }
  if (file.size > HARD_LIMIT_BYTES) {
    setFocusMode(true);
    const gb = (file.size / 1024 / 1024 / 1024).toFixed(1);
    showError(
      root,
      `That file is ${gb} GB. Wave handles files up to about 8 GB in the browser — anything bigger and the wasm engine starts running out of headroom for the encoder's working memory.`
    );
    return;
  }
  if (file.size > WARN_LIMIT_BYTES) {
    const gb = (file.size / 1024 / 1024 / 1024).toFixed(1);
    const proceed = confirm(
      `${gb} GB is a large file for in-browser conversion. It may take a while, and browsers without File System Access (Firefox / Safari) will need enough RAM to hold the output. Continue?`
    );
    if (!proceed) {
      goIdle(root);
      return;
    }
  }

  // AVIF / HEIC / HEIF still-images use the same ISO Base Media File Format
  // container as MP4. ffmpeg will "decode" the single frame they hold as a
  // 1-frame video, and the resulting 1-second MP4 is bewildering junk. The
  // sync gate above lets these through (an .avif renamed .mp4 has an accepted
  // extension), so a byte-level peek at the ftyp brand is the reliable stop.
  if (await looksLikeImageContainer(file)) {
    setFocusMode(true);
    showError(
      root,
      `"${file.name}" is an image file (AVIF / HEIC / HEIF) inside an MP4-style container, not a video. Wave converts moving pictures — drop in a real video and it'll take it.`
    );
    return;
  }

  currentFile = file;
  setFocusMode(true);
  handleInspect(root, file).catch((err) => {
    console.error('[wave] inspect failed', err);
    showError(root, humanizeError(err));
  });
}

function looksLikeVideo(file: File): boolean {
  // Animated GIF is a valid video source but carries MIME image/gif, which
  // would otherwise hit the image/ reject below. Check it before MIME sniffing.
  if (/\.gif$/i.test(file.name)) return true;
  // MIME hint wins when the browser filled it in.
  if (file.type) {
    if (file.type.startsWith('video/')) return true;
    if (
      file.type.startsWith('audio/') ||
      file.type.startsWith('image/') ||
      file.type.startsWith('text/') ||
      file.type === 'application/pdf'
    ) {
      return false;
    }
  }
  if (KNOWN_VIDEO_EXT.test(file.name)) return true;
  if (NON_VIDEO_EXT.test(file.name)) return false;
  // Unknown extension, unknown MIME: give ffmpeg the chance to sniff it. If it
  // turns out to be audio-only, the inspect stage catches that and reports it.
  return true;
}

function isLikelyAudio(file: File): boolean {
  if (file.type?.startsWith('audio/')) return true;
  return /\.(mp3|m4a|aac|wav|flac|ogg|opus|wma|aiff|aif|amr|ac3|eac3|dts|mka|caf|au|ra|ape|tta|wv)$/i.test(file.name);
}

function isLikelyPlaylist(file: File): boolean {
  return PLAYLIST_EXT.test(file.name);
}

/** ISO Base Media File Format brands used by still-image containers. AVIF,
 *  HEIC / HEIF, and their sequence variants all sit inside the same box
 *  structure as MP4 — ffmpeg is willing to decode the single AV1 or HEVC
 *  frame they hold as a 1-frame "video," and the resulting 1-second MP4 is
 *  useless. Reject them before inspect so the user gets a clear message
 *  instead of a bewildering tiny file. */
const IMAGE_ISOBMFF_BRANDS = new Set([
  'avif', 'avis',        // AVIF still + sequence
  'heic', 'heix',        // HEIC (HEVC-encoded still)
  'mif1', 'msf1',        // HEIF still + sequence
  'heim', 'heis',        // HEIF multiview
  'hevm', 'hevs',        // HEIF HEVC multiview
  'j2ki',                // JPEG 2000 image
]);

/** Peeks the file's ftyp box and returns true when its major brand names a
 *  still-image container. Returns false on read errors or on non-ISO-BMFF
 *  files (which have no ftyp box to speak of). */
async function looksLikeImageContainer(file: File): Promise<boolean> {
  try {
    const head = new Uint8Array(await file.slice(0, 32).arrayBuffer());
    // ftyp box layout: 4 bytes size, 4 bytes "ftyp", 4 bytes major brand.
    if (head.length < 12) return false;
    const isFtyp = head[4] === 0x66 && head[5] === 0x74 && head[6] === 0x79 && head[7] === 0x70;
    if (!isFtyp) return false;
    const brand = String.fromCharCode(head[8], head[9], head[10], head[11]).trim();
    return IMAGE_ISOBMFF_BRANDS.has(brand.toLowerCase());
  } catch {
    return false;
  }
}

async function handleInspect(root: HTMLElement, file: File): Promise<void> {
  // If the engine isn't ready yet, show a warming UI once the wait crosses
  // the threshold — quick loads never flash the intermediate stage.
  let warmingShown = false;
  const warmingTimer = window.setTimeout(() => {
    if (!engineReady) {
      warmingShown = true;
      renderWarming(root, file.name);
    }
  }, WARMING_UI_DELAY_MS);

  try {
    // Kick off the WebCodecs probe alongside inspect — the probe result feeds
    // into the plan so the callout can say "WebCodecs H.264" up-front instead
    // of promising mpeg4 and switching backends silently at convert time.
    const [info, support] = await Promise.all([
      inspectFile(file),
      probeWebCodecs().catch(() => null),
    ]);
    engineReady = true;

    if (!info.video && !info.audio) {
      throw new Error(
        "That file loaded but doesn't seem to contain any video or audio streams."
      );
    }
    // Format-specific pages (mp4-to-mp3, mkv-to-mp3, etc.) still reject
    // audio-only sources because the promise on those pages is about a video
    // container. The audio-video-to-mp3 hub sets ACCEPT_AUDIO and skips this
    // gate so a plain .wav / .m4a / .flac / .wma can flow through to the MP3
    // extract path.
    if (!info.video && !ACCEPT_AUDIO) {
      throw new Error(
        `The file only contains audio (${info.audio?.codec ?? 'unknown codec'}). This page works from a video source — for audio-only sources, use the /audio-video-to-mp3/ page instead.`
      );
    }

    currentInfo = info;
    const inputName = `input.${extensionOf(file.name)}`;
    // Audio-only sources can only produce MP3; flip the preselected format so
    // the inspect UI doesn't try to plan an impossible MP4 conversion.
    const initialFormat: OutputFormat = !info.video ? 'mp3' : PRESELECT_FORMAT;
    const options = lastOptions?.file === file ? lastOptions.options : presetOptions();

    renderInspect(root, info, {
      initial: initialFormat,
      options,
      focus: TOOL,
      replan: (fmt, opts) => planConversion(info, inputName, fmt, support, opts),
      onConvert: (fmt, opts, streamTarget) => {
        lastOptions = { file, options: opts };
        runConversion(root, file, fmt, opts, streamTarget);
      },
      onPickAnother: () => goIdle(root),
    });
  } finally {
    window.clearTimeout(warmingTimer);
    void warmingShown; // referenced for readability
  }
}

function runConversion(
  root: HTMLElement,
  file: File,
  format: OutputFormat,
  planOptions: PlanOptions,
  streamTarget?: FileSystemWritableFileStream,
): void {
  currentAbort = new AbortController();
  const abortSignal = currentAbort.signal;

  const handle = renderConverting(root, file.name, format, {
    onCancel: () => {
      // Cooperative abort — the ffmpeg worker stays warm for the next run so
      // there's no wasm re-load cost after cancelling.
      currentAbort?.abort();
      // Best-effort: close the FSA stream we were writing into, otherwise
      // the browser leaves a zero-byte file behind on disk.
      if (streamTarget) streamTarget.abort().catch(() => { /* ignore */ });
    },
  });
  // Nudge the bar to 2% right away so the user sees Wave has started work
  // even before ffmpeg emits its first progress event.
  handle.setProgress(0.02);

  convert(file, {
    format,
    signal: abortSignal,
    // Reserve the first 2% of the bar for setup (mount + engine warm-up) so
    // the UI shows life immediately. Real ffmpeg progress fills 2% → 100%.
    onProgress: (fraction) => handle.setProgress(0.02 + fraction * 0.98),
    precomputedInfo: currentInfo ?? undefined,
    streamTarget,
    planOptions,
  })
    .then(async (result) => {
      handle.setProgress(1);
      handle.dispose();
      if (streamTarget) {
        // FSA path: the muxer already wrote every byte to the user's file
        // handle; all we need to do is close the stream so the OS commits
        // it. The done panel then reads `result.blob.size === 0` and shows
        // "saved directly" instead of a Save button.
        try { await streamTarget.close(); } catch { /* ignore */ }
      }
      renderDone(root, result, {
        onConvertAnother: () => goIdle(root),
        onSave: () => triggerDownload(result.blob, result.outputName),
      });
    })
    .catch(async (err) => {
      handle.dispose();
      if (streamTarget) {
        // Abort the writable so the OS doesn't leave a truncated / empty
        // file behind. Best-effort — the stream may already be closed.
        try { await streamTarget.abort(); } catch { /* ignore */ }
      }
      if (err instanceof CancelledError || abortSignal.aborted) {
        goIdle(root);
        return;
      }
      console.error('[wave] convert failed', err);
      showError(root, humanizeError(err));
    })
    .finally(() => {
      currentAbort = null;
    });
}

function showError(root: HTMLElement, message: string): void {
  renderError(root, message, {
    onTryAgain: () => {
      if (currentFile) void acceptFile(root, currentFile);
      else goIdle(root);
    },
    onPickAnother: () => goIdle(root),
  });
}

function humanizeError(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err);
  if (/out of memory|maximum call stack|memory access|allocation failed|abort\(\)/i.test(raw)) {
    return 'The file was too big for the browser to hold in memory. Try a smaller file or a shorter clip.';
  }
  if (/moov atom not found/i.test(raw)) {
    return "This MP4 looks truncated — the header (moov atom) is missing. The file may have been cut off mid-download.";
  }
  // DRM / encryption markers. Order matters: check the DRM-specific patterns
  // BEFORE the generic "invalid data" one so protected files get the right
  // message instead of the "corrupt or unknown container" catch-all.
  if (
    /\bcenc\b|\bcbcs\b|\bpiff\b|\bplayready\b|widevine|fairplay|\bwmdrm\b|\bdrm\b|protected content|encrypted stream|encryption scheme/i
      .test(raw)
  ) {
    return "This file appears to be DRM-protected (encrypted). Wave can't decode protected media — nothing in a browser can. Try a source that isn't rented / studio-encrypted.";
  }
  if (/invalid data found|no such file or directory|does not contain any stream/i.test(raw)) {
    return "Wave couldn't recognise this as a media file. It may be corrupt, encrypted, or use a container the engine doesn't handle.";
  }
  // Codec names ffmpeg-wasm's LGPL build can't handle. Mostly ancient or
  // studio codecs — surface a message that names the actual codec so the
  // user knows what to look for.
  const codecMatch = raw.match(/(?:decoder|codec|not (?:currently )?supported|unsupported).*?\b(sorenson|spark|flv1|cinepak|indeo|smc|jpeg2000|prores(?:_ks|_kostya)?|dnxhd|dnxhr|hap|truehd|dts-hd|dtshd|atmos|wmapro|wmavoice|nellymoser|amr|mpeg-1 video)\b/i);
  if (codecMatch) {
    return `Wave's engine doesn't ship a decoder for ${codecMatch[1]}. This codec needs specialised or GPL-licensed software that can't run in the browser.`;
  }
  if (/no video/i.test(raw) && /and no audio/i.test(raw)) {
    return "The file loaded, but no video or audio streams were found inside it.";
  }
  if (/exited with code/i.test(raw)) {
    return "The engine couldn't complete this conversion. The source may use a codec Wave's fallback encoder can't reach.";
  }
  return raw;
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap, { once: true });
} else {
  bootstrap();
}
